import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { Button, makeStyles, Portal, tokens, Tooltip } from "@fluentui/react-components";
import {
  ArrowNext16Regular,
  ArrowPrevious16Regular,
  ChevronDown12Regular,
  CursorClick20Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";

import { findStepElement, HELP_PANEL_ATTR } from "./dom";
import { HelpStepJump } from "./HelpStepJump";
import { cardPosition, rectOf, type Rect } from "./position";
import type { HelpLanguage, HelpStep } from "./types";
import { groupStepsBySection, localizedStep } from "./types";
import { ACCENT, SHADOW_CARD, uiText, Z } from "./ui";

/**
 * The guided tour: a spotlight over the real page, stepping through the guide's steps.
 *
 * Hand-rolled rather than a tour library — it needs a rectangle, a card and a few buttons, and
 * the things a library fights you on (staying inside an open dialog, keeping Escape from closing
 * that dialog, a step counter that is a button) are exactly what this needs.
 *
 * The caller passes ONLY steps that are currently on screen; each one is re-checked as the tour
 * advances, because a step can disappear mid-tour and a spotlight on nothing is worse than a
 * skipped step. The step counter opens a searchable jump list and the section arrows skip a block
 * at a time, so the walk is not only linear.
 */

const CARD_WIDTH = 360;
/** Space to reserve for the card when deciding whether it fits below the target. */
const CARD_HEIGHT = 200;

const useStyles = makeStyles({
  spotlight: {
    position: "fixed",
    borderRadius: "8px",
    // One box with an enormous spread shadow: dims and blocks the page while leaving the target
    // itself untouched and readable.
    boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
    outline: `2px solid ${ACCENT}`,
    outlineOffset: "2px",
    pointerEvents: "auto",
    transitionProperty: "top, left, width, height",
    transitionDuration: "120ms",
    transitionTimingFunction: "ease",
  },
  card: {
    position: "fixed",
    width: `${CARD_WIDTH}px`,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderRadius: "16px",
    boxShadow: SHADOW_CARD,
    padding: "16px",
  },
  topRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  counter: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "4px",
    padding: "2px 4px",
    marginLeft: "-4px",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1.2px",
    color: tokens.colorBrandForeground1,
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  topActions: { display: "flex", alignItems: "center", marginRight: "-6px" },
  section: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "2px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  title: { fontSize: "14.5px", fontWeight: tokens.fontWeightSemibold, marginTop: "4px" },
  body: {
    fontSize: "13px",
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
    lineHeight: 1.55,
  },
  actions: { display: "flex", alignItems: "center", gap: "8px", marginTop: "14px" },
  inspect: { fontSize: "11.5px", marginLeft: "-4px" },
  spacer: { flex: 1 },
});

interface HelpTourProps {
  language: HelpLanguage;
  steps: HelpStep[];
  /**
   * The subtree the tour is confined to — the open dialog, when the tour was started over one.
   * Every lookup is scoped to it.
   */
  scope?: HTMLElement | null;
  /** Where to enter the walk. The drawer's "start here" and the inspector both pick a step. */
  startIndex?: number;
  /** `PageHelp.sections` — the order the jump list and the section arrows follow. */
  sections?: string[];
  /** Hands over to click-to-explain; the tour ends. */
  onInspect?: () => void;
  onClose: () => void;
}

export const HelpTour = ({
  steps,
  scope = null,
  startIndex = 0,
  sections,
  language,
  onInspect,
  onClose,
}: HelpTourProps) => {
  const styles = useStyles();
  const text = uiText(language);
  const [index, setIndex] = useState(startIndex);
  const [rect, setRect] = useState<Rect | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);

  const step = steps[index];
  const isLast = index === steps.length - 1;
  const spoken = step ? localizedStep(step, language) : null;

  const goTo = useCallback(
    (next: number): void => {
      if (next < 0 || next >= steps.length) return;
      setIndex(next);
    },
    [steps.length]
  );

  const groups = useMemo(() => groupStepsBySection(sections, steps), [sections, steps]);
  const groupIndex = groups.findIndex((group) => step && group.steps.includes(step));

  /** Skip to the first step of the neighbouring section. */
  const skipSection = (delta: number): void => {
    const target = groups[groupIndex + delta]?.steps[0];
    if (target) goTo(steps.indexOf(target));
  };

  // Measure before paint so the spotlight never renders one frame in the wrong place.
  useLayoutEffect(() => {
    if (!step) return;
    // A detached scope means the dialog was closed mid-tour; nothing inside it can be found, so
    // the tour walks off the end and closes rather than spotlighting the page behind it.
    const lookup = scope ?? document;
    const el = findStepElement(step.selector, lookup);
    if (!el) {
      if (isLast) onClose();
      else setIndex((i) => i + 1);
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setRect(rectOf(el));

    // Follow the element while the smooth scroll settles (~40 frames), then on any later reflow.
    let frames = 0;
    let raf = 0;
    const track = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
      if (++frames < 40) raf = window.requestAnimationFrame(track);
    };
    raf = window.requestAnimationFrame(track);

    const remeasure = (): void => {
      const current = findStepElement(step.selector, lookup);
      if (current) setRect(rectOf(current));
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [step, isLast, onClose, scope]);

  useEffect(() => {
    /**
     * Capture phase, and the handled keys are stopped here: a tour started over a dialog sits ON
     * TOP of it, and both listen for Escape — without capture, ending the tour also dismissed the
     * half-filled form beneath it. While the jump list is open, Escape closes only the list and
     * the arrows are left to the caret in its search box.
     */
    const onKey = (e: KeyboardEvent): void => {
      if (jumpOpen) {
        if (e.key !== "Escape") return;
        e.stopPropagation();
        e.preventDefault();
        setJumpOpen(false);
        return;
      }
      if (e.key !== "Escape" && e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.stopPropagation();
      e.preventDefault();
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goTo(index + 1);
      else goTo(index - 1);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [goTo, index, jumpOpen, onClose]);

  if (!step || !rect) return null;
  const card = cardPosition(rect, { width: CARD_WIDTH, height: CARD_HEIGHT });

  return (
    <Portal>
      <div
        onClick={onClose}
        className={styles.spotlight}
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
          zIndex: Z.spotlight,
        }}
      />
      <div
        role="dialog"
        // Excluded from dialog detection: the tour's own card is never the work surface.
        {...{ [HELP_PANEL_ATTR]: "tour" }}
        aria-label={text.tourAria(index + 1, steps.length, spoken?.title ?? step.title)}
        className={styles.card}
        style={{ top: card.top, left: card.left, zIndex: Z.card }}
      >
        <div className={styles.topRow}>
          <HelpStepJump
            open={jumpOpen}
            onOpenChange={setJumpOpen}
            steps={steps}
            sections={sections}
            language={language}
            currentIndex={index}
            onPick={goTo}
            trigger={
              // The counter is the way OUT of the linear walk, so it has to look pressable.
              <button
                type="button"
                className={styles.counter}
                data-help="help-tour-jump"
                aria-label={text.stepCounterAria(index + 1, steps.length)}
                aria-haspopup="dialog"
              >
                {text.stepCounter(index + 1, steps.length)}
                <ChevronDown12Regular />
              </button>
            }
          />

          <div className={styles.topActions}>
            {groups.length > 1 && (
              <>
                <Tooltip content={text.prevSection} relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ArrowPrevious16Regular />}
                    disabled={groupIndex <= 0}
                    onClick={() => skipSection(-1)}
                  />
                </Tooltip>
                <Tooltip content={text.nextSection} relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ArrowNext16Regular />}
                    disabled={groupIndex < 0 || groupIndex >= groups.length - 1}
                    onClick={() => skipSection(1)}
                  />
                </Tooltip>
              </>
            )}
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss16Regular />}
              onClick={onClose}
              aria-label={text.endTour}
            />
          </div>
        </div>

        {step.section && <div className={styles.section}>{step.section}</div>}

        <div className={styles.title}>{spoken?.title ?? step.title}</div>
        <div className={styles.body}>{spoken?.body ?? step.body}</div>

        <div className={styles.actions}>
          {onInspect && (
            <Button
              appearance="transparent"
              size="small"
              icon={<CursorClick20Regular />}
              onClick={onInspect}
              data-help="help-tour-inspect"
              className={styles.inspect}
            >
              {text.inspect}
            </Button>
          )}
          <span className={styles.spacer} />
          <Button
            appearance="subtle"
            size="small"
            onClick={() => goTo(index - 1)}
            disabled={index === 0}
          >
            {text.back}
          </Button>
          <Button
            appearance="primary"
            size="small"
            onClick={() => (isLast ? onClose() : goTo(index + 1))}
          >
            {isLast ? text.done : text.next}
          </Button>
        </div>
      </div>
    </Portal>
  );
};
